/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Parametres\MoussesList.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT GESTION MOUSSES

📋 DESCRIPTION:
Composant React pour la gestion complète du catalogue des mousses.
Interface d'administration avec calculs automatiques de prix au m³.

🎯 RESPONSABILITÉS:
- Gestion complète des mousses avec dimensions et prix
- Calculs automatiques prix au m³ selon type (HT NET vs TTC PUBLIC)
- Interface CRUD avec formulaire de saisie avancé
- Gestion des marges et remises fournisseurs
- Aperçu en temps réel des calculs de prix
- Validation des données avec contraintes métier

🔗 DÉPENDANCES:
- MousseService: Service de gestion des mousses
- AuthContext: Contexte d'authentification utilisateur
- React: Hooks useState, useEffect, useContext pour gestion d'état

📡 INTERACTIONS:
- MousseService: CRUD mousses via API backend
- AuthContext: Vérification authentification utilisateur
- Interface: Formulaire avec calculs temps réel
- Validation: Contraintes sur dimensions et prix

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Calculs automatiques prix m³ selon type prix (HT NET/TTC PUBLIC)
- Aperçu temps réel des calculs dans formulaire
- Gestion marges (coefficient) vs remises (pourcentage)
- Interface responsive avec tableaux adaptatifs
- Validation authentification obligatoire
- Messages de feedback pour actions utilisateur
*/
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { mousseService, Mousse, MousseSaisie } from '../../services/MousseService';
import AuthContext from '../../contexts/AuthContext';

const MoussesList: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mousses, setMousses] = useState<Mousse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingMousse, setEditingMousse] = useState<Mousse | null>(null);

  // État du formulaire
  const [formData, setFormData] = useState<MousseSaisie>({
    nom: '',
    prixSaisi: 0,
    longueur: 200,
    largeur: 120,
    epaisseur: 10,
    typePrix: 'TTC_PUBLIC',
    remiseFournisseur: 0,
    coefficientMarge: 1.5,
    fournisseur: ''
  });

  // Charger les mousses
  useEffect(() => {
    loadMousses();
  }, []);

  const loadMousses = async () => {
    try {
      setLoading(true);
      const data = await mousseService.getMousses();
      setMousses(data);
    } catch (error) {
      console.error('Erreur lors du chargement des mousses:', error);
      setError('Erreur lors du chargement des mousses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (editingMousse) {
        // Modification
        await mousseService.updateMousse(editingMousse.id!, formData);
        setSuccessMessage('Mousse modifiée avec succès!');
      } else {
        // Création
        await mousseService.saveMousse(formData);
        setSuccessMessage('Mousse ajoutée avec succès!');
      }

      // Recharger la liste
      await loadMousses();
      
      // Réinitialiser le formulaire
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de la mousse');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleEdit = (mousse: Mousse) => {
    setEditingMousse(mousse);
    setFormData({
      nom: mousse.nom,
      prixSaisi: mousse.prixSaisi,
      longueur: mousse.dimensionsPlaque.longueur,
      largeur: mousse.dimensionsPlaque.largeur,
      epaisseur: mousse.dimensionsPlaque.epaisseur,
      typePrix: mousse.typePrix,
      remiseFournisseur: mousse.remiseFournisseur || 0,
      coefficientMarge: mousse.coefficientMarge || 1.5,
      fournisseur: mousse.fournisseur || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette mousse ?')) {
      try {
        setLoading(true);
        await mousseService.deleteMousse(id);
        setSuccessMessage('Mousse supprimée avec succès!');
        await loadMousses();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression de la mousse');
      } finally {
        setLoading(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prixSaisi: 0,
      longueur: 200,
      largeur: 120,
      epaisseur: 10,
      typePrix: 'TTC_PUBLIC',
      remiseFournisseur: 0,
      coefficientMarge: 1.5,
      fournisseur: ''
    });
    setEditingMousse(null);
    setShowForm(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'typePrix' ? value : (isNaN(Number(value)) ? value : Number(value))
    });
  };

  // Calcul du prix au m³ en temps réel
  const calculerPrixM3Preview = (): number => {
    const volume = (formData.longueur / 100) * (formData.largeur / 100) * (formData.epaisseur / 100);
    
    if (formData.typePrix === 'HT_NET') {
      const coeffMarge = formData.coefficientMarge || 1.5;
      const prixVentePlaque = formData.prixSaisi * coeffMarge;
      return prixVentePlaque / volume;
    } else {
      const prixVenteM3TTC = formData.prixSaisi / volume;
      return prixVenteM3TTC / 1.2;
    }
  };

  if (!isAuthenticated) {
    // Rediriger vers la page de login après un court délai
    setTimeout(() => {
      navigate('/login');
    }, 2000);
    
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Redirection vers la page de connexion...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium text-gray-900">Gestion des Mousses</h2>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary"
          disabled={loading}
        >
          + Ajouter une mousse
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingMousse ? 'Modifier la mousse' : 'Ajouter une nouvelle mousse'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nom */}
              <div className="md:col-span-2">
                <label htmlFor="nom" className="form-label">
                  Nom de la mousse *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  required
                  className="input-field"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Ex: 35A/10 MOUSSE BULTEX 35 A"
                />
              </div>

              {/* Dimensions */}
              <div>
                <label htmlFor="longueur" className="form-label">
                  Longueur plaque (cm) *
                </label>
                <input
                  type="number"
                  id="longueur"
                  name="longueur"
                  required
                  min="1"
                  className="input-field"
                  value={formData.longueur}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="largeur" className="form-label">
                  Largeur plaque (cm) *
                </label>
                <input
                  type="number"
                  id="largeur"
                  name="largeur"
                  required
                  min="1"
                  className="input-field"
                  value={formData.largeur}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="epaisseur" className="form-label">
                  Épaisseur (cm) *
                </label>
                <input
                  type="number"
                  id="epaisseur"
                  name="epaisseur"
                  required
                  min="1"
                  className="input-field"
                  value={formData.epaisseur}
                  onChange={handleChange}
                />
              </div>

              {/* Prix */}
              <div>
                <label htmlFor="prixSaisi" className="form-label">
                  Prix par plaque (€) *
                </label>
                <input
                  type="number"
                  id="prixSaisi"
                  name="prixSaisi"
                  required
                  min="0"
                  step="0.01"
                  className="input-field"
                  value={formData.prixSaisi}
                  onChange={handleChange}
                />
              </div>

              {/* Type de prix */}
              <div>
                <label htmlFor="typePrix" className="form-label">
                  Type de prix *
                </label>
                <select
                  id="typePrix"
                  name="typePrix"
                  required
                  className="input-field"
                  value={formData.typePrix}
                  onChange={handleChange}
                >
                  <option value="TTC_PUBLIC">TTC PUBLIC</option>
                  <option value="HT_NET">HT NET</option>
                </select>
              </div>

              {/* Remise ou coefficient selon le type */}
              {formData.typePrix === 'TTC_PUBLIC' ? (
                <div>
                  <label htmlFor="remiseFournisseur" className="form-label">
                    Remise fournisseur (%)
                  </label>
                  <input
                    type="number"
                    id="remiseFournisseur"
                    name="remiseFournisseur"
                    min="0"
                    max="100"
                    step="0.1"
                    className="input-field"
                    value={formData.remiseFournisseur}
                    onChange={handleChange}
                  />
                </div>
              ) : (
                <div>
                  <label htmlFor="coefficientMarge" className="form-label">
                    Coefficient de marge
                  </label>
                  <input
                    type="number"
                    id="coefficientMarge"
                    name="coefficientMarge"
                    min="1"
                    step="0.1"
                    className="input-field"
                    value={formData.coefficientMarge}
                    onChange={handleChange}
                  />
                </div>
              )}

              {/* Fournisseur */}
              <div>
                <label htmlFor="fournisseur" className="form-label">
                  Fournisseur
                </label>
                <input
                  type="text"
                  id="fournisseur"
                  name="fournisseur"
                  className="input-field"
                  value={formData.fournisseur}
                  onChange={handleChange}
                  placeholder="Ex: Bultex France"
                />
              </div>
            </div>

            {/* Aperçu du prix calculé */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Aperçu du calcul :</h4>
              <div className="text-sm text-blue-800">
                <p>Volume plaque : {((formData.longueur / 100) * (formData.largeur / 100) * (formData.epaisseur / 100)).toFixed(3)} m³</p>
                <p className="font-medium">Prix au m³ (pour devis) : {calculerPrixM3Preview().toFixed(2)} € HT</p>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : (editingMousse ? 'Modifier' : 'Ajouter')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des mousses */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Liste des mousses</h3>
        </div>

        {loading && !showForm ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Chargement...</p>
          </div>
        ) : mousses.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            Aucune mousse enregistrée
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dimensions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix au m³
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type Prix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fournisseur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mousses.map((mousse) => (
                  <tr key={mousse.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {mousse.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {mousse.dimensionsPlaque.longueur}×{mousse.dimensionsPlaque.largeur}×{mousse.dimensionsPlaque.epaisseur} cm
                      </div>
                      <div className="text-xs text-gray-500">
                        {((mousse.dimensionsPlaque.longueur / 100) * (mousse.dimensionsPlaque.largeur / 100) * (mousse.dimensionsPlaque.epaisseur / 100)).toFixed(3)} m³
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {mousse.prixM3?.toFixed(2)} € HT
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        mousse.typePrix === 'HT_NET' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {mousse.typePrix === 'HT_NET' ? 'HT NET' : 'TTC PUBLIC'}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        {mousse.typePrix === 'HT_NET' 
                          ? `Marge: ${mousse.coefficientMarge}x`
                          : `Remise: ${mousse.remiseFournisseur}%`
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {mousse.fournisseur || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(mousse)}
                        className="text-indigo-600 hover:text-indigo-900"
                        disabled={loading}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(mousse.id!)}
                        className="text-red-600 hover:text-red-900"
                        disabled={loading}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoussesList;
